/**
 * Creates a stable sort that brings specified values to the top while maintaining relative order
 * @param {Array} arr - The array to sort
 * @param {Array} priorityValues - Values to prioritize at the top of the array
 * @param {Function} [getValue] - Optional function to extract the value to compare (for objects)
 * @returns {Array} New sorted array
 */
export function stablePrioritySort<T, K>(arr: T[], priorityValues: K[], getValue = (x: T): K => x as unknown as K) {
    // Create array of indices for stable sort
    const indexed = arr.map((item, index) => ({ item, index }));

    // Create a Map for O(1) lookup of priority values
    const priorityMap = new Map<K, number>(
        priorityValues.map((val, index) => [val, index])
    );

    // Custom compare function that prioritizes specified values
    const compareWithPriority = (a: { item: T; index: number; }, b: { item: T; index: number; }) => {
        const aValue = getValue(a.item);
        const bValue = getValue(b.item);

        const aHasPriority = priorityMap.has(aValue);
        const bHasPriority = priorityMap.has(bValue);

        if (aHasPriority && bHasPriority) {
            // If both are priority values, sort by original order
            return a.index - b.index
        } else if (aHasPriority) {
            // If only a is priority, it comes first
            return -1;
        } else if (bHasPriority) {
            // If only b is priority, it comes first
            return 1;
        } else {
            // If neither is priority, maintain original order
            return a.index - b.index;
        }
    };

    // Sort and map back to original values
    return indexed.sort(compareWithPriority).map(({ item }) => item);
}
